import { Component } from 'react';
import { connect } from 'react-redux';
import Tooltip from '../components/Tooltip/Tooltip';
import { getPublicMediaRoot } from './../serviceAgent';
import { fixedEncodeURIComponent, getLetterGradeFromNumberGrade } from './../GlobalFunctions';
import { countCompletionStatuses } from './../completionStatus';
import { selectClassroom } from '../Classrooms';
import { setUnit } from '../Session';
import styles from '../SCSS/LibraryCard.module.scss';
import utilityStyles from '../SCSS/common/utilityStyles.module.scss';
import ExploreButton from '../components/LibraryCardButtons/ExploreButton';
import CompletedButton from '../components/LibraryCardButtons/CompletedButton';
import ResumeButton from '../components/LibraryCardButtons/ResumeButton';
import LockedButton from '../components/LibraryCardButtons/LockedButton';
import UnlockedButton from '../components/LibraryCardButtons/UnlockedButton';
import type { ExtractReturn } from '../types';
import blueStar from '../images/pkp-star-blue.svg';

const TrialStar = () => (
  <Tooltip content='You have access to this entire unit during your trial period' direction='right'>
    <div className={styles.trialStar}>
      <img alt='Blue star' src={blueStar} />
      <div className={styles.starAttention} />
    </div>
  </Tooltip>
);

/*
Component: LibraryCard

The LibraryCard component generates the cards used to create the library
Required Props:
hours = (int)
standards = (int)
unit = (string)
content = (string)
*/
// NOTE: cleanView is the view of the Library Card on the myPeekaville Units page
const mapStateToProps = (state) => ({
  classrooms: {
    state: state.classrooms.state,
    list: state.classrooms.classrooms,
    selectedClassroom: state.classrooms.selectedClassroom,
  },
  classroom: selectClassroom(state),
});

const mapDispatchToProps = (dispatch) => ({
  setUnit: (newUnit) => dispatch(setUnit(newUnit)),
});

type Props = ExtractReturn<typeof mapStateToProps> & ExtractReturn<typeof mapDispatchToProps>;
type State = {
  isStarted: boolean;
  isCompleted: boolean;
  percentComplete: number;
};

class LibraryCard extends Component<Props, State> {
  constructor(props) {
    super(props);
    this.state = {
      isStarted: false,
      isCompleted: false,
      percentComplete: 0,
    };
  }

  computeCompletionStatus = () => {
    const myCompletionStatusKey = `${this.props.unit}-${getLetterGradeFromNumberGrade(this.props.classroom.grade)}`;
    const myCompletionStatus = this.props.classroom.completionStatus[myCompletionStatusKey];
    const myStatuses = countCompletionStatuses(myCompletionStatus);
    const percentage = Math.round((myStatuses.Completed / myStatuses.Total) * 100);
    this.setState({
      percentComplete: percentage,
    });

    if (myStatuses.InProgress > 0 || myStatuses.Completed > 0) {
      this.setState({
        isStarted: true,
      });
    } else {
      this.setState({
        isStarted: false,
      });
    }

    if (percentage === 100) {
      this.setState({
        isStarted: false,
        isCompleted: true,
      });
    }
  };

  componentDidMount() {
    if (this.props.classroom) {
      this.computeCompletionStatus();
    }
  }

  componentDidUpdate(prevProps, prevState) {
    if (prevProps.classroom !== this.props.classroom) {
      this.computeCompletionStatus();
    }
  }

  handleOnUnlockUnit = () => {
    this.props.callbackOnUnlockUnit(this.props.cardPosition);
  };
  handleOnLockUnit = () => {
    this.props.callbackOnLockUnit(this.props.cardPosition);
  };
  // use disabled unit to toggle
  getClickHandlerBasedOnDisabledStatus = () => {
    if (this.state.isStarted) {
      return (
        <div className={styles.LibraryCardButtonLine}>
          <ResumeButton onClick={() => this.openUnit()} percentComplete={this.state.percentComplete} />
        </div>
      );
    } else if (this.state.isCompleted) {
      return (
        <div className={styles.LibraryCardButtonLine}>
          <CompletedButton onClick={() => this.openUnit()} />
        </div>
      );
    }

    if (!this.props.cleanView) {
      return (
        <div className={styles.LibraryCardButtonLine}>
          <ExploreButton onClick={() => this.openUnit()} />
        </div>
      );
    }
  };
  getStorybookCoverUrl = () => {
    const { classroom } = this.props;
    const language = classroom.language;
    let storybookCoverUrl;

    if (language && language !== 'en') {
      storybookCoverUrl = `${getPublicMediaRoot()}images/thumbnails/${language}${this.props.unit}Bookshelf.png`;
    } else {
      storybookCoverUrl = `${getPublicMediaRoot()}images/thumbnails/${this.props.unit}Bookshelf.png`;
    }

    return storybookCoverUrl;
  };

  render() {
    return (
      <div className={styles.LibraryCard}>
        <div
          className={`${styles.cardContainer} ${
            this.props.cleanView ? styles.cleanViewCardHeight : styles.libraryViewCardHeight
          }`}
        >
          {/*Find a way to comment this out for the cleanView*/}
          {this.showTrialStarIfTrialMode()}
          <div className={`${styles.coverImage} background-bluelibrarycard`}>
            {this.props.classroom ? (
              <div
                className={`${styles.imgWrapper} ${utilityStyles.absCentre} ${
                  !this.props.disableUnit ? styles['like-button'] : ''
                }`}
                onClick={this.props.disableUnit ? null : this.openUnit}
              >
                {this.props.classroom && <img alt='Storybook cover' src={this.getStorybookCoverUrl()} />}
              </div>
            ) : (
              <div className={styles.loadingMessage}>Loading...</div>
            )}
          </div>
          {this.props.cleanView && this.props.locked && <LockedButton onClick={() => this.handleOnUnlockUnit()} />}
          {this.props.cleanView && !this.props.locked && <UnlockedButton onClick={() => this.handleOnLockUnit()} />}
          <div className={styles.textWrapper}>
            <div className={styles.title}>{this.props.displayName || this.props.unit}</div>
            <div className={styles.description}>{this.props.content}</div>
          </div>
          <div className={styles.buttonWrapper}>
            {this.props.cleanView ? null : this.getClickHandlerBasedOnDisabledStatus()}
          </div>
        </div>
      </div>
    );
  }

  showTrialStarIfTrialMode() {
    return this.props.trialUser && <TrialStar />;
  }

  openUnit = () => {
    const { classroom } = this.props;
    const grade = getLetterGradeFromNumberGrade(classroom.grade);
    const classroomName = classroom.className;
    this.props.setUnit(this.props.unit);
    this.props.history.push(`/${fixedEncodeURIComponent(classroomName)}/lessonPlan/${this.props.unit}-${grade}`);
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(LibraryCard);
